import { useEffect, useState } from "preact/hooks"
import type { ContractTermBasis, ContractTermInput, ContractTermStatus } from "@/api/contract_term"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { BASIS_OPTIONS, ContractTermForm, empty_term_form, form_to_input, is_percentage_basis } from "./contract_term_format"

interface ContractTermDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Form to start from; a blank confirmed term when absent. */
  initial?: ContractTermForm
  title: string
  onSave: (input: ContractTermInput) => Promise<void>
}

function input_value(e: Event): string {
  return (e.target as HTMLInputElement | HTMLTextAreaElement).value
}

interface FieldProps {
  label: string
  hint?: string
  className?: string
  children: preact.ComponentChildren
}

function Field({ label, hint, className, children }: FieldProps) {
  return (
    <div class={`space-y-1 ${className ?? ""}`}>
      <Label>{label}</Label>
      {children}
      {hint && <p class="text-xs text-gray-500">{hint}</p>}
    </div>
  )
}

/** Add or edit one contract term. */
export function ContractTermDialog({ open, onOpenChange, initial, title, onSave }: ContractTermDialogProps) {
  const [form, set_form] = useState<ContractTermForm>(initial ?? empty_term_form())
  const [error, set_error] = useState<string | null>(null)
  const [saving, set_saving] = useState(false)

  useEffect(() => {
    if (open) {
      set_form(initial ?? empty_term_form())
      set_error(null)
    }
  }, [open])

  function set_field<K extends keyof ContractTermForm>(key: K, value: ContractTermForm[K]) {
    set_form((prev: ContractTermForm) => ({ ...prev, [key]: value }))
  }

  async function handle_save() {
    const result = form_to_input(form)
    if ("error" in result) {
      set_error(result.error)
      return
    }
    set_saving(true)
    set_error(null)
    try {
      await onSave(result.input)
      onOpenChange(false)
    } catch (e) {
      set_error(e instanceof Error ? e.message : "Failed to save term")
    } finally {
      set_saving(false)
    }
  }

  const pct = is_percentage_basis(form.basis)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent class="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Chargebacks billed under this program are checked against the signed rate.</DialogDescription>
        </DialogHeader>

        <div class="grid grid-cols-2 gap-4 py-2">
          <Field label="Program">
            <Input value={form.program} placeholder="SCHF DC" onChange={(e: Event) => set_field("program", input_value(e))} />
          </Field>
          <Field label="Match pattern" hint="Case-insensitive regex matched against the charge's program label.">
            <Input
              value={form.match_pattern}
              placeholder="SCHF\s*DC"
              class="font-mono"
              onChange={(e: Event) => set_field("match_pattern", input_value(e))}
            />
          </Field>
          <Field label="Basis">
            <Select value={form.basis} onValueChange={(value: string) => set_field("basis", value as ContractTermBasis)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BASIS_OPTIONS.map((option: { value: ContractTermBasis; label: string }) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={pct ? "Rate (%)" : "Amount ($)"}>
            <Input value={form.rate} placeholder={pct ? "1.17" : "30000"} onChange={(e: Event) => set_field("rate", input_value(e))} />
          </Field>
          <Field label="Retailer" hint="Leave blank if the term applies to every retailer.">
            <Input value={form.retailer_name} placeholder="Loblaw" onChange={(e: Event) => set_field("retailer_name", input_value(e))} />
          </Field>
          <Field label="Minimum per line ($)">
            <Input value={form.minimum_amount} placeholder="1000" onChange={(e: Event) => set_field("minimum_amount", input_value(e))} />
          </Field>
          <Field label="Effective from">
            <Input type="date" value={form.effective_from} onChange={(e: Event) => set_field("effective_from", input_value(e))} />
          </Field>
          <Field label="Effective to">
            <Input type="date" value={form.effective_to} onChange={(e: Event) => set_field("effective_to", input_value(e))} />
          </Field>
          {form.basis === "pct_of_shorted_value" && (
            <>
              <Field label="Only while fill rate below (%)">
                <Input value={form.threshold_pct} placeholder="98" onChange={(e: Event) => set_field("threshold_pct", input_value(e))} />
              </Field>
              <Field label="Rolling window (weeks)">
                <Input
                  value={form.threshold_window_weeks}
                  placeholder="13"
                  onChange={(e: Event) => set_field("threshold_window_weeks", input_value(e))}
                />
              </Field>
            </>
          )}
          <Field label="Status">
            <Select value={form.status} onValueChange={(value: string) => set_field("status", value as ContractTermStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="suggested">Suggested</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Source excerpt" hint="Verbatim quote from the contract." className="col-span-2">
            <Textarea value={form.source_excerpt} rows={2} onChange={(e: Event) => set_field("source_excerpt", input_value(e))} />
          </Field>
          <Field label="Notes" className="col-span-2">
            <Textarea value={form.notes} rows={2} onChange={(e: Event) => set_field("notes", input_value(e))} />
          </Field>
        </div>

        {error && <p class="text-sm text-red-600">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handle_save} disabled={saving}>
            {saving ? "Saving..." : "Save term"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
